<template>
  <div class="w-full max-w-7xl mx-auto px-4 space-y-6">
    <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Snapshot Orders</h1>
        <p class="text-sm text-gray-600 mt-1">Completed Snapshot orders and free claims, with the products selected for each image.</p>
      </div>
      <div class="grid gap-3 w-full lg:w-[42rem] md:grid-cols-3">
        <label class="block md:col-span-1">
          <span class="text-sm font-medium text-gray-700">University</span>
          <select
            v-model="selectedUniversityId"
            class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            @change="loadUniversity"
          >
            <option value="">Select a university</option>
            <option v-for="uni in universities" :key="uni.id" :value="uni.id">{{ uni.name }}</option>
          </select>
        </label>
        <label class="block md:col-span-1">
          <span class="text-sm font-medium text-gray-700">Ceremony</span>
          <select
            v-model="selectedCeremonyId"
            class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            :disabled="!selectedUniversityId"
            @change="loadOrders"
          >
            <option value="">All ceremonies</option>
            <option v-for="ceremony in ceremonies" :key="ceremony.id" :value="ceremony.id">{{ formatCeremony(ceremony) }}</option>
          </select>
        </label>
        <div class="flex items-end">
          <button
            type="button"
            class="w-full rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            :disabled="loading || !selectedUniversityId"
            @click="loadOrders"
          >
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="error" class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {{ error }}
    </div>

    <div v-if="selectedUniversityId" class="grid grid-cols-2 lg:grid-cols-5 gap-3">
      <div v-for="card in summaryCards" :key="card.label" class="rounded-lg border border-gray-200 bg-white p-4">
        <div class="text-xs text-gray-500">{{ card.label }}</div>
        <div class="mt-1 text-2xl font-semibold text-gray-900">{{ card.value }}</div>
      </div>
    </div>

    <div class="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div class="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
        <div>
          <h2 class="font-semibold text-gray-900">Completed Orders</h2>
          <p class="text-xs text-gray-500">{{ scopeLabel }}</p>
        </div>
        <div class="text-xs text-gray-500">{{ orders.length }} shown</div>
      </div>
      <div v-if="loading && !orders.length" class="p-8 text-center text-sm text-gray-600">
        Loading orders...
      </div>
      <div v-else-if="!selectedUniversityId" class="p-8 text-center text-sm text-gray-600">
        Select a university to view Snapshot orders.
      </div>
      <div v-else-if="!orders.length" class="p-8 text-center text-sm text-gray-600">
        No completed Snapshot orders found for this scope.
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 text-left text-gray-500">
            <tr>
              <th class="px-4 py-2 font-medium">Order</th>
              <th class="px-4 py-2 font-medium">Amount</th>
              <th class="px-4 py-2 font-medium">Items</th>
              <th class="px-4 py-2 font-medium">Delivery</th>
              <th class="px-4 py-2 font-medium">Print export</th>
              <th class="px-4 py-2 font-medium">Latest activity</th>
              <th class="px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="order in orders" :key="order.order_id" class="align-top hover:bg-gray-50">
              <td class="px-4 py-3">
                <div class="font-mono text-xs text-gray-900">{{ order.order_nano_id }}</div>
                <div class="mt-1 text-xs text-gray-500">{{ order.stripe_status }}</div>
              </td>
              <td class="px-4 py-3 text-xs">
                <div class="font-medium text-gray-900">{{ formatMoney(order.total_amount, order.currency) }}</div>
                <div v-if="order.delivery_amount" class="text-gray-500">Delivery {{ formatMoney(order.delivery_amount, order.currency) }}</div>
              </td>
              <td class="px-4 py-3 text-xs">
                <div>{{ order.digital_count || 0 }} digital</div>
                <div>{{ order.print_count || 0 }} print</div>
              </td>
              <td class="px-4 py-3 text-xs">
                <div>{{ deliveryMethod(order) }}</div>
                <div class="text-gray-500">{{ deliveryStatus(order) }}</div>
                <div v-if="deliveryLocation(order)" class="text-gray-500">{{ deliveryLocation(order) }}</div>
              </td>
              <td class="px-4 py-3 text-xs">
                <div>{{ order.print_exported_count || 0 }} / {{ order.print_count || 0 }}</div>
                <div v-if="order.print_export_batches" class="text-gray-500">{{ order.print_export_batches }}</div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-600">
                {{ formatDate(latestActivity(order)) }}
              </td>
              <td class="px-4 py-3 text-right">
                <button type="button" class="text-sm font-medium text-blue-700 hover:text-blue-900" @click="openOrder(order)">
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="selectedOrder" class="fixed inset-0 z-40">
      <button type="button" class="absolute inset-0 bg-black/30" aria-label="Close order details" @click="selectedOrder = null"></button>
      <aside class="absolute right-0 top-0 h-full w-full max-w-3xl overflow-y-auto bg-white shadow-xl">
        <div class="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">{{ selectedOrder.order_nano_id }}</h2>
            <p class="text-xs text-gray-500">Created {{ formatDate(selectedOrder.created_at) }}</p>
          </div>
          <button type="button" class="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50" @click="selectedOrder = null">
            Close
          </button>
        </div>

        <div class="space-y-5 p-5">
          <div class="grid md:grid-cols-3 gap-3">
            <div class="rounded-md border border-gray-200 p-3">
              <div class="text-xs text-gray-500">Total</div>
              <div class="mt-1 font-semibold text-gray-900">{{ formatMoney(selectedOrder.total_amount, selectedOrder.currency) }}</div>
            </div>
            <div class="rounded-md border border-gray-200 p-3">
              <div class="text-xs text-gray-500">Delivery</div>
              <div class="mt-1 font-semibold text-gray-900">{{ deliveryMethod(selectedOrder) }}</div>
              <div class="text-xs text-gray-500">{{ deliveryStatus(selectedOrder) }}</div>
            </div>
            <div class="rounded-md border border-gray-200 p-3">
              <div class="text-xs text-gray-500">Address summary</div>
              <div class="mt-1 font-semibold text-gray-900">{{ deliveryLocation(selectedOrder) || 'None' }}</div>
              <div class="text-xs text-gray-500">{{ deliverySource(selectedOrder) }}</div>
            </div>
          </div>

          <div>
            <h3 class="text-sm font-semibold text-gray-900 mb-3">Ordered images and products</h3>
            <div class="space-y-3">
              <div
                v-for="item in selectedOrder.items"
                :key="item.item_id"
                class="flex gap-3 rounded-md border border-gray-200 p-3"
              >
                <div class="h-20 w-24 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100">
                  <img
                    v-if="item.thumbnail_url"
                    :src="item.thumbnail_url"
                    :alt="item.file_name || item.photo_id || item.product_label"
                    class="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">{{ productTypeLabel(item.product_type) }}</span>
                    <span class="text-sm font-semibold text-gray-900">{{ item.product_label }}</span>
                  </div>
                  <div class="mt-1 text-xs text-gray-500 font-mono truncate">{{ item.file_name || item.photo_id || 'image' }}</div>
                  <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span>{{ formatMoney(item.line_total, selectedOrder.currency) }}</span>
                    <span v-if="item.product_code">{{ item.product_code }}</span>
                    <span v-if="item.print_exported_at">Exported {{ formatDate(item.print_exported_at) }}</span>
                    <span v-else-if="item.product_type === 'PRINT'">Print export pending</span>
                  </div>
                  <div v-if="optionText(item)" class="mt-2 text-xs text-gray-600">
                    {{ optionText(item) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { trackEvent } from '../../Analytics.vue'

export default {
  name: 'SnapshotOrders',
  data() {
    return {
      universities: [],
      ceremonies: [],
      selectedUniversityId: this.$route.query.university_id || '',
      selectedCeremonyId: this.$route.query.ceremony_id || '',
      summary: {},
      orders: [],
      selectedOrder: null,
      loading: false,
      error: '',
    }
  },
  computed: {
    selectedUniversity() {
      return this.universities.find((u) => u.id === this.selectedUniversityId) || null
    },
    selectedCeremony() {
      return this.ceremonies.find((c) => c.id === this.selectedCeremonyId) || null
    },
    ordersEndpoint() {
      const base = `${this.apiBase()}/admin/snapshot/orders/universities/${this.selectedUniversityId}`
      if (this.selectedCeremonyId) return `${base}/ceremonies/${this.selectedCeremonyId}`
      return base
    },
    scopeLabel() {
      if (!this.selectedUniversityId) return 'No university selected'
      const university = this.selectedUniversity ? this.selectedUniversity.name : this.selectedUniversityId
      if (!this.selectedCeremonyId) return `${university} · all ceremonies`
      return `${university} · ${this.formatCeremony(this.selectedCeremony)}`
    },
    summaryCards() {
      return [
        { label: 'Completed orders', value: this.summary.completed_orders || 0 },
        { label: 'Digital items', value: this.summary.digital_items || 0 },
        { label: 'Print items', value: this.summary.print_items || 0 },
        { label: 'Downloaded/collected', value: this.summary.downloaded_orders || 0 },
        { label: 'Pending print export', value: this.summary.pending_print_exports || 0 },
      ]
    },
  },
  created() {
    this.loadUniversities()
    trackEvent(this.$route.path, { label: 'SNAPSHOT_ORDERS_OPENED' })
  },
  methods: {
    apiBase() {
      return process.env.VUE_APP_SNAPSHOT_API_BASE || ''
    },
    async loadUniversities() {
      this.error = ''
      try {
        const r = await axios.get('/universities')
        this.universities = r.data || []
        if (!this.selectedUniversityId && this.universities.length) {
          this.selectedUniversityId = this.universities[0].id
        }
        if (this.selectedUniversityId) await this.loadUniversity()
      } catch (e) {
        this.error = 'Unable to load universities.'
      }
    },
    async loadUniversity() {
      this.error = ''
      this.selectedOrder = null
      this.ceremonies = []
      if (!this.selectedUniversityId) return
      try {
        const r = await axios.get(`/universities/${this.selectedUniversityId}/ceremonies`)
        this.ceremonies = r.data || []
        if (this.selectedCeremonyId && !this.ceremonies.some((c) => c.id === this.selectedCeremonyId)) {
          this.selectedCeremonyId = ''
        }
        await this.loadOrders()
      } catch (e) {
        this.error = 'Unable to load ceremonies.'
      }
    },
    async loadOrders() {
      if (!this.selectedUniversityId) return
      this.error = ''
      this.loading = true
      this.selectedOrder = null
      this.syncRoute()
      try {
        const r = await axios.get(this.ordersEndpoint)
        this.summary = r.data.summary || {}
        this.orders = r.data.items || []
      } catch (e) {
        this.error = 'Unable to load Snapshot orders.'
        this.summary = {}
        this.orders = []
      } finally {
        this.loading = false
      }
    },
    syncRoute() {
      const query = {}
      if (this.selectedUniversityId) query.university_id = this.selectedUniversityId
      if (this.selectedCeremonyId) query.ceremony_id = this.selectedCeremonyId
      this.$router.replace({ query }).catch(() => {})
    },
    openOrder(order) {
      this.selectedOrder = order
    },
    formatCeremony(ceremony) {
      if (!ceremony) return this.selectedCeremonyId || ''
      if (ceremony.label) return ceremony.label
      const value = ceremony.datetime || ceremony.ceremony_datetime || ceremony.id || ''
      try { return new Date(value).toLocaleString() } catch (e) { return String(value) }
    },
    formatDate(value) {
      if (!value) return ''
      try { return new Date(value).toLocaleString() } catch (e) { return String(value) }
    },
    formatMoney(value, currency = 'gbp') {
      if (value === null || value === undefined || value === '') return 'Free'
      const amount = Number(value || 0) / 100
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: String(currency || 'gbp').toUpperCase(),
        }).format(amount)
      } catch (e) {
        return `${String(currency || 'gbp').toUpperCase()} ${amount.toFixed(2)}`
      }
    },
    productTypeLabel(value) {
      if (value === 'DIGITAL') return 'Digital'
      if (value === 'PRINT') return 'Print'
      return value || 'Item'
    },
    deliveryMethod(order) {
      return (order.delivery_summary && order.delivery_summary.method) || 'none'
    },
    deliveryStatus(order) {
      return (order.delivery_summary && order.delivery_summary.status) || 'not_required'
    },
    deliveryLocation(order) {
      const summary = order.delivery_summary || {}
      return [summary.country, summary.postcode].filter(Boolean).join(' ')
    },
    deliverySource(order) {
      const summary = order.delivery_summary || {}
      if (!summary.has_address) return 'No address stored'
      if (summary.source === 'stripe_checkout') return 'Collected by Stripe'
      return 'Address collected'
    },
    latestActivity(order) {
      return order.last_print_exported_at || order.last_downloaded_at || order.collected_at || order.created_at
    },
    optionText(item) {
      const options = item.item_options || {}
      return Object.entries(options)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => `${this.optionLabel(key)}: ${value}`)
        .join(' · ')
    },
    optionLabel(key) {
      return String(key || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    },
  },
}
</script>
